/**
 * ── LIBRARY READINESS — which handles gate `playing`, and the load-phase HUD's counters (§4.3) ──
 *
 * The boot ladder (`world/loadPhase.ts`) gains exactly one new counter — "library handles in the
 * boot lane" — and nothing else changes. This module is that counter's arithmetic, in the SHAPE
 * of `world/readiness.ts` (a predicate over a set, folded into a flat report), because it feeds
 * the same gate: `residency()` answers for colliders, `bootGate()` answers for library sources,
 * and `mount` ANDs them.
 *
 * ⚠️ ONLY THE `boot` LANE GATES. A `gameplay` or `prefetch` handle that is still fetching when the
 * gate is asked is exactly the design (§4.3 — swap-in on `ready`, never an await in the tick).
 *
 * ⚠️ A FAILED HANDLE DOES NOT HOLD THE GATE OPEN. `world/readiness.ts`'s L3.B lesson, applied
 * before it can recur: one missing crate cost twenty seconds of a frozen world because "bound
 * nothing" read as "still coming". A boot-lane handle that settled `failed` is `broken` — the
 * level starts around it with the kind's placeholder policy (§4.2) — never `pending`.
 *
 * PURE (`PURE_DIRS`): a fold over a list of `{ id, lane, state }` views. It reads no handle object
 * and no clock; the service projects its handles into views and hands them over.
 */

import { isSettledHandleState, type AssetLane, type HandleState } from './types';

/** What readiness needs to know about a handle — the service projects this from `AssetHandle`. */
export interface HandleView {
  id: string;
  lane: AssetLane;
  state: HandleState;
}

export interface BootGateReport {
  /** Every boot-lane handle id, in the order given (deduplicated). */
  required: readonly string[];
  /** Boot-lane ids still `queued`/`fetching`/`parsing` — what a loading line would name. */
  pending: readonly string[];
  /** Boot-lane ids that settled `failed` — never coming; the level starts around them. */
  broken: readonly string[];
  /** Boot-lane ids that are `ready` (or were released after being ready). */
  resident: readonly string[];
  /** `true` when nothing in the boot lane is still coming. THE gate. */
  allResident: boolean;
}

/**
 * Which handles gate `playing`: the boot lane, and only the boot lane. A handle listed twice
 * (two acquires of one id) counts once, in its most-advanced state — a `ready` beside a
 * `queued` for the same id is one source, resident.
 */
export function bootGate(handles: Iterable<HandleView>): BootGateReport {
  const byId = new Map<string, HandleState>();
  for (const h of handles) {
    if (h.lane !== 'boot') continue;
    const held = byId.get(h.id);
    if (held === undefined || rank(h.state) > rank(held)) byId.set(h.id, h.state);
  }
  const required: string[] = [];
  const pending: string[] = [];
  const broken: string[] = [];
  const resident: string[] = [];
  for (const [id, state] of byId) {
    required.push(id);
    if (state === 'failed') broken.push(id);
    else if (isSettledHandleState(state)) resident.push(id);
    else pending.push(id);
  }
  return { required, pending, broken, resident, allResident: pending.length === 0 };
}

/** The predicate alone. */
export function allBootResident(handles: Iterable<HandleView>): boolean {
  return bootGate(handles).allResident;
}

export interface HandleCounters {
  queued: number;
  /** In flight: `fetching` AND `parsing` — the HUD has no use for the split, the dev hook does. */
  fetching: number;
  ready: number;
  failed: number;
}

/**
 * The load-phase HUD's four numbers, over every lane or one. `released` handles are not counted
 * anywhere — they are gone. Not deduplicated: two handles on one source are two handles, which
 * is what "N things are loading" means to a screen.
 */
export function handleCounters(handles: Iterable<HandleView>, lane?: AssetLane): HandleCounters {
  const c: HandleCounters = { queued: 0, fetching: 0, ready: 0, failed: 0 };
  for (const h of handles) {
    if (lane !== undefined && h.lane !== lane) continue;
    switch (h.state) {
      case 'queued':
        c.queued += 1;
        break;
      case 'fetching':
      case 'parsing':
        c.fetching += 1;
        break;
      case 'ready':
        c.ready += 1;
        break;
      case 'failed':
        c.failed += 1;
        break;
      case 'released':
        break;
    }
  }
  return c;
}

/** Progress along the machine — used only to pick the most-advanced view of a duplicated id. */
function rank(s: HandleState): number {
  switch (s) {
    case 'queued':
      return 0;
    case 'fetching':
      return 1;
    case 'parsing':
      return 2;
    case 'failed':
      return 3;
    case 'ready':
      return 4;
    case 'released':
      return 5;
  }
}
